import type { DataTablePagination } from '@atrvd/saas-ui'
import type { DbError, DbResult } from '@/lib/db-tenant'

/**
 * Table pagination rules shared by every table in the app: the page sizes a
 * user can pick, the default, and when the footer shows at all. Local tables
 * use them through `useTablePagination` (its `forTotal` for server-paged tables
 * whose total arrives with the page); Daily Banking keeps page and size in the
 * URL (`useBankingFilterParams`) and uses these helpers directly.
 */

/** Page sizes offered by the footer's selector. */
export const PAGE_SIZE_OPTIONS = [20, 50, 100] as const

/** The largest page a paged read serves — the top of {@link PAGE_SIZE_OPTIONS}. */
export const MAX_PAGE_SIZE = Math.max(...PAGE_SIZE_OPTIONS)

/** Page size a table starts with (and falls back to for an invalid URL value). */
export const DEFAULT_PAGE_SIZE = 20

/**
 * Whether a table shows its pagination footer (page buttons + size selector).
 *
 * Keyed on the record count, not the page count: with 30 rows at size 50 there
 * is one page, but the footer must stay so the user can switch back to 20.
 * At ≤ 20 records every size shows everything, so the footer is noise.
 */
export function isPaginationVisible(totalItems: number): boolean {
  return totalItems > DEFAULT_PAGE_SIZE
}

/** Last 1-based page for `totalItems` rows; `1` for an empty result, never `0`. */
export function lastPageOf(totalItems: number, pageSize: number): number {
  return Math.max(1, Math.ceil(totalItems / pageSize))
}

/** 1-based page that shows the row at 0-based `index` of the full, sorted list. */
export function pageOfIndex(index: number, pageSize: number): number {
  return Math.floor(index / pageSize) + 1
}

/**
 * Page size from an untrusted value (a URL param). Anything that is not one of
 * {@link PAGE_SIZE_OPTIONS} — missing, garbage, `25` — gives {@link DEFAULT_PAGE_SIZE}.
 */
export function parsePageSize(raw: string | null | undefined): number {
  const size = Number(raw)
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(size) ? size : DEFAULT_PAGE_SIZE
}

/** Inputs of {@link buildDataTablePagination}. */
export interface PaginationState {
  /** Current 1-based page. */
  page: number
  pageSize: number
  /** Rows across all pages. */
  totalItems: number
  onPageChange: (page: number) => void
  /** Must reset the page to 1 — a size change makes the old page number meaningless. */
  onPageSizeChange: (size: number) => void
}

/**
 * The saas-ui `DataTable` `pagination` prop for this state, with the size
 * selector — or `undefined` when {@link isPaginationVisible} says the footer
 * is hidden (the DataTable then renders no footer).
 */
export function buildDataTablePagination({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: PaginationState): DataTablePagination | undefined {
  if (!isPaginationVisible(totalItems)) return undefined
  return {
    currentPage: page,
    totalPages: lastPageOf(totalItems, pageSize),
    pageSize,
    totalItems,
    onPageChange,
    pageSizeOptions: [...PAGE_SIZE_OPTIONS],
    onPageSizeChange,
  }
}

/**
 * The `INVALID_PAGINATION` error for a page that is not an integer >= 1 or a
 * page size that is not an integer in 1..{@link MAX_PAGE_SIZE}, or null when
 * both are valid — the guard every paged tenant read runs before asking the
 * server. `Number.isInteger` first: every comparison with NaN is false, so a
 * bare `< 1` check lets NaN and ±Infinity through as a PostgREST 400 with no
 * actionable message. The cap keeps a page under the PostgREST `max_rows`
 * limit, which would otherwise cut it short silently.
 */
export function pageQueryError(page: number, pageSize: number): DbError | null {
  const validPage = Number.isInteger(page) && page >= 1
  const validSize = Number.isInteger(pageSize) && pageSize >= 1 && pageSize <= MAX_PAGE_SIZE
  if (validPage && validSize) return null
  return {
    message: `Invalid page=${page}, pageSize=${pageSize}: page must be an integer >= 1, pageSize an integer between 1 and ${MAX_PAGE_SIZE}`,
    code: 'INVALID_PAGINATION',
  }
}

/** What a supabase-js read resolves to, narrowed to what {@link totalsRowOf} needs. */
interface RowsResponse<T> {
  data: T[] | null
  error: { message: string; code: string } | null
}

/**
 * The row to read a paged RPC's totals off — the RPCs repeat them on every
 * row. Normally that is the page's first row (or `undefined` for an empty
 * first page: the set is empty). An empty page past the first carries no row:
 * the set shrank under the page, or the page was opened past the end.
 * Reporting 0 would send the table to its empty state, so the first row of
 * the first page is read instead (`readFirstRow` asks for limit 1, offset 0),
 * letting the caller step back to the actual last page.
 */
export async function totalsRowOf<T>(
  page: number,
  pageRows: readonly T[],
  readFirstRow: () => PromiseLike<RowsResponse<T>>,
): Promise<DbResult<T | undefined>> {
  if (pageRows.length > 0 || page === 1) return { data: pageRows[0], error: null }
  const head = await readFirstRow()
  if (head.error) return { data: null, error: { message: head.error.message, code: head.error.code } }
  return { data: (head.data ?? [])[0], error: null }
}
